// ./core/UI/components/BetBox.tsx

import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import type { BetSide } from '@platform/contracts';
import {
  getBetBoxStyles,
  subtextStyle,
  toggleStyles,
} from '../styles/BetBox.styles';
import { useTheme } from '../context/ThemeContext';
import { getImageAsset, isSkySnifferGame } from '../utils/getAssetHelper';
import { getUIConfig, getGameAssetPath } from '../configs/gameUIConfig';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import {
  useT,
  formatAmount,
  formatDecimal,
  formatMoney,
  formatCurrencyUnit,
  shouldUseCurrencySymbol,
  useNumberFormat,
  truncateToDecimals,
} from '@core/i18n';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { SKYX_NEW_NS } from '@core/utils/skyxNewI18n';
import {
  resolveBetPanelInputVisual,
  resolveMainBetChromeButtonStyle,
} from '@core/gameengine/controller/BaseGameController';
import {
  clampAutoCashout,
  clampStake,
  decrementStakeInList,
  incrementStakeInList,
  isStakeAtListMax,
  isStakeAtListMin,
} from '@core/gameengine/config/GameBetLimits';
import { useInstantMainBetTap } from '../hooks/useInstantMainBetTap';
import { useBettingSpacebarShortcut } from '../hooks/useBettingSpacebarShortcut';
import { useLongPress } from '../hooks/useLongPress';
import { useGamingSessionStore } from '@core/regulatory';

type BetButtonStateKey = 'bet' | 'cancel' | 'cashOut' | 'cashoutFailed';

const BET_BUTTON_IMAGE_BY_STATE: Record<BetButtonStateKey, string> = {
  bet: 'GreenBetButton.png',
  cancel: 'Cancel.png',
  cashOut: 'Cashout.png',
  cashoutFailed: 'GreenBetButton.png',
};

const activeStyle: React.CSSProperties = {
  transform: 'translateY(1px)',
  boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.08)',
};

const truncate2 = (num: number) => {
  return Math.floor(num * 100) / 100;
};

export const BetBox: React.FC<{ side: BetSide; controller: any }> = ({
  side,
  controller,
}) => {
  // Spacebar bet/cashout hotkey (panel A only).
  // skyx_new / sky-x: disabled for every jurisdiction.
  useBettingSpacebarShortcut(
    side === 'A' && !isSkyxNewRoute(controller?.getGameSlug?.() ?? '')
      ? controller
      : null,
    'A'
  );

  // -------------------------------
  // ADDED: PERSISTENT PREVIOUS STATE
  // -------------------------------
  const previousStateRef = useRef<'armed' | null>(null);
  // --- ADDED: Ref to store user's preferred cashout to prevent unwanted resets ---
  const lastValidCashoutRef = useRef<number | null>(null);

  const { theme } = useTheme();
  const useGameStore = resolveGameStoreHook(controller);
  const gameSlug = controller.getGameSlug();
  const isSkyxNew = isSkyxNewRoute(gameSlug);
  const t = useT(isSkyxNew ? SKYX_NEW_NS : gameSlug);
  const isSkySniffer = isSkySnifferGame(gameSlug);
  const gameManifest = controller.getAssetManifest?.();
  const platformUIConfig = controller.getPlatformUIConfig?.() || {};
  const autoplayAllowed = useGamingSessionStore(
    s => s.policy.autoplay !== false
  );
  const stakeCounterLoopSound = platformUIConfig.stakeCounterLoopSound;
  const uiProfile = platformUIConfig.profile;
  const mainBetButtonChrome = platformUIConfig.mainBetButtonChrome;
  const autoCashoutToggleChrome = platformUIConfig.autoCashoutToggleChrome;
  const betPanelAutoControlsChrome =
    platformUIConfig.betPanelAutoControlsChrome;
  const betPanelQuickBetChipChrome =
    platformUIConfig.betPanelQuickBetChipChrome;
  const betPanelStakeStepperChrome =
    platformUIConfig.betPanelStakeStepperChrome;
  const betPanelStakeInputChrome = platformUIConfig.betPanelStakeInputChrome;
  const betPanelButtonPressCss = platformUIConfig.betPanelButtonPressCss;
  const betPanelAutoPlayLayout = platformUIConfig.betPanelAutoPlayLayout;
  /** UKGC centering comes from betPanelAutoPlayLayout.autoCashout (config). */
  const centerAutoCashout =
    betPanelAutoPlayLayout?.autoCashout?.center === true;
  const quickBetValues: number[] = platformUIConfig.quickBetValues ?? [
    5, 20, 50, 100,
  ];
  const uiConfig = useMemo(() => getUIConfig(gameSlug), [gameSlug]);
  const betPanelBg = getGameAssetPath(gameSlug, 'betpanelbg.png');
  const deviceType = useGameStore(s => s.deviceType);
  const autoPlayTextOffset =
    betPanelAutoPlayLayout?.text?.[deviceType] ??
    betPanelAutoPlayLayout?.text?.mobile;
  const autoPlayButtonOffset =
    betPanelAutoPlayLayout?.button?.[deviceType] ??
    betPanelAutoPlayLayout?.button?.mobile;
  const autoCashoutToggleLeft =
    betPanelAutoPlayLayout?.autoCashout?.toggleLeft?.[deviceType] ??
    betPanelAutoPlayLayout?.autoCashout?.toggleLeft?.mobile;
  const isLandscape = useGameStore(s => s.isLandscape);
  const isIpad = useGameStore(s => s.isIpad);
  const setIsAuto = useGameStore(s => s.setIsAuto);
  const box = useGameStore(s => s.bets[side]);
  const phase = useGameStore(s => s.phase);
  // Sky-X: hide "Waiting for next round" on CANCEL (same as preloader), but keep it during FLYING
  const suppressWaitingRoundSubtext = isSkyxNew && phase !== 'FLYING';
  const nowX = useGameStore(s => s.nowX);
  const currency = useGameStore(s => s.currency);
  const numberFormat = useNumberFormat(currency);
  const { decimal } = numberFormat.separators;
  const betLimits = useGameStore(s => s.betLimits);
  const autoPlaySettings = useGameStore(s => s.autoPlaySettings[side]);
  const autoPlayProgress = useGameStore(s => s.autoPlayProgress[side]);
  const betPanelHandheldHeights = platformUIConfig.betPanelHandheldHeights;
  const betPanelDesktopHeights = platformUIConfig.betPanelDesktopHeights;
  const useHandheldBetPanelHeights =
    isSkyxNew && deviceType !== 'desktop' && betPanelHandheldHeights != null;
  const _useDesktopBetPanelHeights =
    isSkyxNew && deviceType === 'desktop' && betPanelDesktopHeights != null;
  const betPanelBorderless = platformUIConfig.betPanelBorderless === true;
  const styles = useMemo(() => {
    const base = getBetBoxStyles(
      theme,
      deviceType,
      isLandscape,
      isIpad,
      uiProfile
    );
    let merged = betPanelBorderless
      ? {
          ...base,
          betBoxControl: { ...base.betBoxControl, border: 'none' },
          stakeBtn: { ...base.stakeBtn, border: 'none' },
          quickBetBtn: { ...base.quickBetBtn, border: 'none' },
          mainBetContainer: { ...base.mainBetContainer, border: 'none' },
        }
      : base;
    const resolveHandheld = platformUIConfig.resolveBetPanelHandheldStyles;
    if (resolveHandheld && deviceType !== 'desktop') {
      merged = resolveHandheld(merged, deviceType, isLandscape);
    }
    const resolveDesktop = platformUIConfig.resolveBetPanelDesktopStyles;
    if (resolveDesktop && deviceType === 'desktop') {
      merged = resolveDesktop(merged);
    }
    return merged;
  }, [
    theme,
    deviceType,
    isLandscape,
    isIpad,
    uiProfile,
    betPanelBorderless,
    platformUIConfig.resolveBetPanelHandheldStyles,
    platformUIConfig.resolveBetPanelDesktopStyles,
  ]);
  const usesCompactPurpleProfile = uiProfile === 'compactPurple';
  const stakeBtnIconSize = useHandheldBetPanelHeights
    ? '72%'
    : usesCompactPurpleProfile && deviceType === 'desktop'
      ? '72%'
      : usesCompactPurpleProfile
        ? '64%'
        : '100%';

  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false); // Simple long press timer for mobile/tablet visual feedback only
  /** Main bet hover chrome — desktop pointer only (no sticky hover on touch). */
  const isMainBetHoverEnabled = deviceType === 'desktop';
  const mainBetHovered = isMainBetHoverEnabled && hovered;

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoCashoutInputRef = useRef<HTMLInputElement>(null);

  const isAutoPlayActiveForThisSide = autoPlaySettings !== null;
  const [localStakeValue, setLocalStakeValue] = useState<string>('');
  const [isEditingStake, setIsEditingStake] = useState<boolean>(false);
  const [localAutoCashoutValue, setLocalAutoCashoutValue] =
    useState<string>('');
  const [isEditingAutoCashout, setIsEditingAutoCashout] =
    useState<boolean>(false);
  const [lastQuickBet, setLastQuickBet] = useState<number | null>(null); // Only active states — no hover

  const [isMinusActive, setIsMinusActive] = useState(false);
  const [isPlusActive, setIsPlusActive] = useState(false);
  const plusAssetPath = getImageAsset(
    gameSlug,
    'autoplayPlusActive',
    gameManifest
  );
  const minusAssetPath = getImageAsset(
    gameSlug,
    'autoplayMinusActive',
    gameManifest
  );
  const autoplayPlusImageUrl = controller.getAssetUrl(plusAssetPath);
  const autoplayMinusImageUrl = controller.getAssetUrl(minusAssetPath); // -------------------------------
  // PREVIOUS STATE UPDATE LOGIC HERE
  // -------------------------------
  useEffect(() => {
    if (!box) return;
    if (box.status === 'armed') {
      previousStateRef.current = 'armed';
    } else if (
      box.status === 'placing' &&
      previousStateRef.current === 'armed'
    ) {
      previousStateRef.current = 'armed';
    } else {
      previousStateRef.current = null;
    }
  }, [box?.status]);

  useEffect(() => {
    if (!isEditingStake) {
      setLocalStakeValue(
        box?.stake !== undefined
          ? numberFormat.formatEditableAmount(box.stake)
          : ''
      );
    }
  }, [box?.stake, isEditingStake, numberFormat]);

  // ---------------------------------------------------------
  // UPDATED: Sync Auto Cashout Logic (Preserves user value)
  // ---------------------------------------------------------
  useEffect(() => {
    if (isEditingAutoCashout) return;

    const serverValue = box?.autoCashoutX;

    if (serverValue === undefined || serverValue === null) return;

    if (
      lastValidCashoutRef.current !== null &&
      lastValidCashoutRef.current !== 1.1 &&
      Math.abs(serverValue - 1.1) < 0.01 // Check if serverValue is ~1.1
    ) {
      const restoreValue = lastValidCashoutRef.current;
      if (
        box.status === 'idle' &&
        Math.abs(serverValue - restoreValue) >= 0.01
      ) {
        useGameStore.getState().upBet(side, { autoCashoutX: restoreValue });
      }
      return; // Stop here, do not update local state to 1.10
    }

    // 3. Normal Sync: If values differ significantly, update local state
    setLocalAutoCashoutValue(numberFormat.formatEditableAmount(serverValue));

    // Update our cache if the server value is not 1.1 (or if cache is empty)
    if (
      Math.abs(serverValue - 1.1) >= 0.01 ||
      lastValidCashoutRef.current === null
    ) {
      lastValidCashoutRef.current = serverValue;
    }
  }, [
    box?.autoCashoutX,
    isEditingAutoCashout,
    box?.status,
    side,
    useGameStore,
    numberFormat,
  ]);

  useEffect(() => {
    if (autoplayAllowed) return;
    if (isAutoPlayActiveForThisSide) {
      useGameStore.getState().stopAutoPlay(side);
    }
  }, [autoplayAllowed, isAutoPlayActiveForThisSide, side, useGameStore]);

  useEffect(() => {
    if (deviceType === 'desktop' || !box) return;
    // Clear sticky hover/press on touch devices after state changes
    if (
      box.status === 'idle' ||
      box.status === 'cashed' ||
      box.status === 'lost' ||
      box.status === 'cancelling'
    ) {
      setPressed(false);
      setHovered(false);
    }

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, [box?.status, deviceType, box]);

  const isBetboxDisabled =
    !box ||
    box.status === 'placing' ||
    box.status === 'placed' ||
    box.status === 'armed' ||
    box.status === 'cashing' ||
    box.status === 'cancelling' ||
    isAutoPlayActiveForThisSide;

  // Auto Cash Out is independent of Auto Play — only lock it while a bet is in flight.
  const isAutoCashoutControlsDisabled =
    !box ||
    box.status === 'placing' ||
    box.status === 'placed' ||
    box.status === 'armed' ||
    box.status === 'cashing' ||
    box.status === 'cancelling';

  const stakeValues = betLimits.stakeValues;
  const isStakeAtMin = !box
    ? true
    : stakeValues?.length
      ? isStakeAtListMin(box.stake, stakeValues)
      : truncateToDecimals(box.stake) <= truncateToDecimals(betLimits.minBet);
  const isStakeAtMax = !box
    ? true
    : stakeValues?.length
      ? isStakeAtListMax(box.stake, stakeValues)
      : truncateToDecimals(box.stake) >= truncateToDecimals(betLimits.maxBet);
  const isStakeDecreaseDisabled = isBetboxDisabled || isStakeAtMin;
  const isStakeIncreaseDisabled = isBetboxDisabled || isStakeAtMax;

  const activeStakeStepperRef = useRef<'increase' | 'decrease' | null>(null);

  const stopStakeCounterLoop = useCallback(() => {
    activeStakeStepperRef.current = null;
    if (!stakeCounterLoopSound) return;
    controller?.getAudio?.()?.stopLoop(stakeCounterLoopSound);
  }, [controller, stakeCounterLoopSound]);

  const tryStartStakeCounterLoop = useCallback(
    (direction: 'increase' | 'decrease', disabled: boolean) => {
      if (!stakeCounterLoopSound || disabled) return;
      const audio = controller?.getAudio?.();
      if (!audio || audio.isSfxMuted()) return;
      activeStakeStepperRef.current = direction;
      audio.noteUserGesture();
      audio.playLoop(stakeCounterLoopSound);
    },
    [controller, stakeCounterLoopSound]
  );

  const increaseStake = useCallback(() => {
    const { betLimits: limits } = useGameStore.getState();
    const currentState = useGameStore.getState().bets[side];
    const newStake = limits.stakeValues?.length
      ? incrementStakeInList(currentState.stake, limits.stakeValues)
      : clampStake(
          truncateToDecimals(currentState.stake + limits.stakeStep),
          limits
        );
    if (newStake === currentState.stake) {
      stopStakeCounterLoop();
      return;
    }
    useGameStore.getState().upBet(side, { stake: newStake });
  }, [side, stopStakeCounterLoop]);

  const decreaseStake = useCallback(() => {
    const { betLimits: limits } = useGameStore.getState();
    const currentState = useGameStore.getState().bets[side];
    const newStake = limits.stakeValues?.length
      ? decrementStakeInList(currentState.stake, limits.stakeValues)
      : clampStake(
          truncateToDecimals(currentState.stake - limits.stakeStep),
          limits
        );
    if (newStake === currentState.stake) {
      stopStakeCounterLoop();
      return;
    }
    useGameStore.getState().upBet(side, { stake: newStake });
  }, [side, stopStakeCounterLoop]);

  const increaseLongPressSound = useMemo(
    () =>
      stakeCounterLoopSound
        ? {
            onPressStart: () =>
              tryStartStakeCounterLoop('increase', isStakeIncreaseDisabled),
            onPressEnd: stopStakeCounterLoop,
          }
        : undefined,
    [
      stakeCounterLoopSound,
      isStakeIncreaseDisabled,
      stopStakeCounterLoop,
      tryStartStakeCounterLoop,
    ]
  );

  const decreaseLongPressSound = useMemo(
    () =>
      stakeCounterLoopSound
        ? {
            onPressStart: () =>
              tryStartStakeCounterLoop('decrease', isStakeDecreaseDisabled),
            onPressEnd: stopStakeCounterLoop,
          }
        : undefined,
    [
      stakeCounterLoopSound,
      isStakeDecreaseDisabled,
      stopStakeCounterLoop,
      tryStartStakeCounterLoop,
    ]
  );

  useEffect(() => {
    const active = activeStakeStepperRef.current;
    if (
      (active === 'decrease' && isStakeDecreaseDisabled) ||
      (active === 'increase' && isStakeIncreaseDisabled)
    ) {
      stopStakeCounterLoop();
    }
  }, [isStakeDecreaseDisabled, isStakeIncreaseDisabled, stopStakeCounterLoop]);

  useEffect(() => {
    return () => {
      stopStakeCounterLoop();
    };
  }, [stopStakeCounterLoop]);

  const increasePressHandlers = useLongPress(
    increaseStake,
    100,
    0.8,
    300,
    increaseLongPressSound
  );
  const decreasePressHandlers = useLongPress(
    decreaseStake,
    100,
    0.8,
    300,
    decreaseLongPressSound
  );

  // Hooks must run before any early return. Action/disabled are synced via refs
  // after btnState/handleClick are computed below.
  const mainBetTapActionRef = useRef<() => void>(() => {});
  const mainBetTapDisabledRef = useRef(true);
  const useInstantMainBetTapEnabled = isSkyxNew && deviceType === 'mobile';
  const { bindPress: bindMainBetPress, useInstantTap: useMainBetInstantTap } =
    useInstantMainBetTap({
      enabled: useInstantMainBetTapEnabled,
      isDisabled: mainBetTapDisabledRef.current,
      onAction: () => {
        if (mainBetTapDisabledRef.current) return;
        mainBetTapActionRef.current();
      },
      fireOnPointerDown: true,
    });

  if (!box) return null;

  const formatAmountWithCurrency = (amount: number) => {
    if (shouldUseCurrencySymbol()) {
      return (
        <span
          style={
            usesCompactPurpleProfile
              ? { fontSize: styles.currencyStyle.fontSize }
              : undefined
          }
        >
          {formatMoney(amount, currency)}
        </span>
      );
    }
    return (
      <>
        <span
          style={
            usesCompactPurpleProfile
              ? { fontSize: styles.currencyStyle.fontSize }
              : undefined
          }
        >
          {formatAmount(amount, currency)}
        </span>
        <span style={{ ...styles.currencyStyle }}>{currency}</span>
      </>
    );
  };

  /** Sky-x new: match live cashout preview to the HUD multiplier (2dp), not raw tick precision. */
  const getLiveCashoutAmount = (stake: number, multiplier: number) => {
    if (isSkyxNew) {
      const displayMultiplier = truncateToDecimals(multiplier);
      return truncateToDecimals(stake * displayMultiplier);
    }
    return truncate2(stake * multiplier);
  };

  const getButtonState = () => {
    const stakeText = formatAmountWithCurrency(box.stake);
    const waitingRoundSubtext = suppressWaitingRoundSubtext
      ? undefined
      : t('ui.hud.waiting_for_next_round');

    const isBettingPhase =
      phase === 'WAITING' ||
      phase === 'FLYING' ||
      phase === 'REVEAL' ||
      phase === 'FLEW_AWAY';
    const isCancelPhase =
      phase === 'WAITING' || phase === 'REVEAL' || phase === 'FLEW_AWAY';

    switch (box.status) {
      case 'idle':
      case 'cashed':
      case 'lost':
        if (isAutoPlayActiveForThisSide) {
          return {
            stateKey: 'cancel' as const,
            valueText: waitingRoundSubtext,
            color:
              pressed &&
              (deviceType === 'mobile' ||
                (deviceType === 'tablet' && isLandscape))
                ? theme.colors.betCancelPressMobile
                : mainBetHovered
                  ? theme.colors.betCancelHover
                  : (theme.colors.betCancel ?? theme.colors.danger),
            borderColor: !pressed ? theme.colors.cancelborder : '',
            boxShadow: pressed
              ? theme.colors.betCancelPress
              : (theme.colors.betCancelShadow ?? ''),
            action: () => {
              useGameStore.getState().stopAutoPlay(side);
            },
            disabled: false,
            isSubtext: Boolean(waitingRoundSubtext),
          };
        }
        return {
          stateKey: 'bet' as const,
          valueText: stakeText,
          color:
            pressed &&
            (deviceType === 'mobile' ||
              (deviceType === 'tablet' && isLandscape))
              ? theme.colors.betButtonPressMobile
              : mainBetHovered
                ? theme.colors.betButtonHover
                : (theme.colors.betIdle ?? theme.colors.betbutton),
          borderColor: pressed
            ? theme.colors.betborderPress
            : theme.colors.betborder,
          boxShadow: pressed ? theme.colors.betButtonPress : '',
          action: () => controller.resolveBetAction(side),
          disabled: !isBettingPhase,
        };

      case 'armed':
        return {
          stateKey: 'cancel' as const,
          valueText: waitingRoundSubtext,
          color:
            pressed &&
            (deviceType === 'mobile' ||
              (deviceType === 'tablet' && isLandscape))
              ? theme.colors.betCancelPressMobile
              : mainBetHovered
                ? theme.colors.betCancelHover
                : (theme.colors.betCancel ?? theme.colors.danger),
          borderColor: !pressed ? theme.colors.cancelborder : '',
          boxShadow: pressed
            ? theme.colors.betCancelPress
            : (theme.colors.betCancelShadow ?? ''),
          action: () => {
            if (isAutoPlayActiveForThisSide) {
              console.log(
                '[BetBox] Auto-play active, stopping before canceling bet.',
                side
              );
              useGameStore.getState().stopAutoPlay(side);
            }
            controller.cancelBet(side);
          },
          disabled: !(isCancelPhase || phase === 'FLYING'), // MODIFIED: Allow cancel during FLYING phase
          isSubtext: Boolean(waitingRoundSubtext),
        };

      case 'placing':
        if (
          isAutoPlayActiveForThisSide ||
          previousStateRef.current === 'armed'
        ) {
          return {
            stateKey: 'cancel' as const,
            color:
              pressed &&
              (deviceType === 'mobile' ||
                (deviceType === 'tablet' && isLandscape))
                ? theme.colors.betCancelPressMobile
                : mainBetHovered
                  ? theme.colors.betCancelHover
                  : (theme.colors.betCancel ?? theme.colors.danger),
            borderColor: !pressed ? theme.colors.cancelborder : '',
            boxShadow: pressed
              ? theme.colors.betCancelPress
              : (theme.colors.betCancelShadow ?? ''),
            action: () => useGameStore.getState().stopAutoPlay(side),
            disabled: true,
          };
        }
        return {
          stateKey: 'bet' as const,
          valueText: stakeText,
          color:
            pressed &&
            (deviceType === 'mobile' ||
              (deviceType === 'tablet' && isLandscape))
              ? theme.colors.betButtonPressMobile
              : mainBetHovered
                ? theme.colors.betButtonHover
                : (theme.colors.betIdle ?? theme.colors.betbutton),
          borderColor: pressed
            ? theme.colors.betborderPress
            : theme.colors.betborder,
          boxShadow: pressed ? theme.colors.betButtonPress : '',
          action: () => {},
          disabled: true,
        }; // --- NEW CASE ADDED ---

      case 'cashing':
        const lastCashoutAmount = getLiveCashoutAmount(box.stake, nowX);
        return {
          stateKey: 'cashOut' as const,
          valueText: formatAmountWithCurrency(lastCashoutAmount),
          color:
            pressed &&
            (deviceType === 'mobile' ||
              (deviceType === 'tablet' && isLandscape))
              ? theme.colors.betCashPressMobile
              : mainBetHovered
                ? theme.colors.betCashHover
                : (theme.colors.betCashout ?? theme.colors.warning),
          borderColor: pressed ? '' : theme.colors.cashoutborder,
          boxShadow: pressed ? theme.colors.betCashPress : '',
          action: () => {},
          disabled: true,
        }; // --- END NEW CASE ---
      case 'cancelling':
        return {
          stateKey: 'cancel' as const,
          color:
            pressed &&
            (deviceType === 'mobile' ||
              (deviceType === 'tablet' && isLandscape))
              ? theme.colors.betCancelPressMobile
              : mainBetHovered
                ? theme.colors.betCancelHover
                : (theme.colors.betCancel ?? theme.colors.danger),
          borderColor: !pressed ? theme.colors.cancelborder : '',
          boxShadow: pressed
            ? theme.colors.betCancelPress
            : (theme.colors.betCancelShadow ?? ''),
          action: () => useGameStore.getState().stopAutoPlay(side),
          disabled: true,
        };

      case 'placed':
        if (box.error) {
          return {
            stateKey: 'cashoutFailed' as const,
            valueText: box.error,
            color: theme.colors.danger,
            borderColor: theme.colors.cancelborder,
            action: () => {},
            disabled: true,
            isSubtext: true,
          };
        }
        if (phase === 'FLYING') {
          // Sky-X: cashout only from 1.01x; at 1.00x show 0 instead of stake
          const isCashoutEnabled = isSkyxNew ? nowX >= 1.01 : true;
          const cashoutAmount =
            isSkyxNew && !isCashoutEnabled
              ? 0
              : getLiveCashoutAmount(box.stake, nowX);
          return {
            stateKey: 'cashOut' as const,
            valueText: formatAmountWithCurrency(cashoutAmount),
            color:
              pressed &&
              pressed &&
              (deviceType === 'mobile' ||
                (deviceType === 'tablet' && isLandscape))
                ? theme.colors.betCashPressMobile
                : mainBetHovered
                  ? theme.colors.betCashHover
                  : (theme.colors.betCashout ?? theme.colors.warning),
            borderColor: pressed ? '' : theme.colors.cashoutborder,
            boxShadow: pressed ? theme.colors.betCashPress : '',
            action: () => controller.cashout(side),
            disabled: !isCashoutEnabled,
          };
        }
        if (phase === 'TAKEOFF') {
          // Sky-X: at 1.00x cashout shows 0 instead of stake
          const cashoutAmount = isSkyxNew ? 0 : box.stake * 1.0;
          return {
            stateKey: 'cashOut' as const,
            valueText: formatAmountWithCurrency(cashoutAmount),
            color:
              pressed &&
              (deviceType === 'mobile' ||
                (deviceType === 'tablet' && isLandscape))
                ? theme.colors.betCashPressMobile
                : mainBetHovered
                  ? theme.colors.betCashHover
                  : (theme.colors.betCashout ?? theme.colors.warning),
            borderColor: pressed ? '' : theme.colors.cashoutborder,
            boxShadow: pressed ? theme.colors.betCashPress : '',
            action: () => {},
            disabled: true,
          };
        }
        if (phase === 'BET_LOCK' || phase === 'UNKNOWN') {
          return {
            stateKey: 'cancel' as const,
            color:
              pressed &&
              (deviceType === 'mobile' ||
                (deviceType === 'tablet' && isLandscape))
                ? theme.colors.betCancelPressMobile
                : mainBetHovered
                  ? theme.colors.betCancelHover
                  : (theme.colors.betCancel ?? theme.colors.danger),
            borderColor: !pressed ? theme.colors.cancelborder : '',
            boxShadow: pressed
              ? theme.colors.betCancelPress
              : (theme.colors.betCancelShadow ?? ''),
            action: () => {},
            disabled: true,
          };
        }
        if (phase === 'WAITING') {
          return {
            stateKey: 'cancel' as const,
            valueText: waitingRoundSubtext,
            color:
              pressed &&
              (deviceType === 'mobile' ||
                (deviceType === 'tablet' && isLandscape))
                ? theme.colors.betCancelPressMobile
                : mainBetHovered
                  ? theme.colors.betCancelHover
                  : (theme.colors.betCancel ?? theme.colors.danger),
            borderColor: !pressed ? theme.colors.cancelborder : '',
            boxShadow: pressed
              ? theme.colors.betCancelPress
              : (theme.colors.betCancelShadow ?? ''),
            action: () => {
              if (isAutoPlayActiveForThisSide) {
                useGameStore.getState().stopAutoPlay(side);
              }
              controller.cancelBet(side);
            },
            disabled: false,
            isSubtext: Boolean(waitingRoundSubtext),
          };
        }
        break;
    }

    return {
      stateKey: 'bet' as const,
      valueText: stakeText,
      color:
        pressed &&
        (deviceType === 'mobile' || (deviceType === 'tablet' && isLandscape))
          ? theme.colors.betButtonPressMobile
          : mainBetHovered
            ? theme.colors.betButtonHover
            : (theme.colors.betIdle ?? theme.colors.betbutton),
      borderColor: pressed
        ? theme.colors.betborderPress
        : theme.colors.betborder,
      boxShadow: pressed ? theme.colors.betButtonPress : '',
      action: () => controller.resolveBetAction(side),
      disabled: !isBettingPhase,
    };
  };

  const btnState = getButtonState();
  const buttonLabelByState: Record<BetButtonStateKey, string> = {
    bet: t('ui.control_panel.bet_panel.bet'),
    cancel: t('ui.common.cancel'),
    cashOut: t('ui.control_panel.action.cash_out'),
    cashoutFailed: t('ui.control_panel.action.cashout_failed'),
  };
  const displayButtonText =
    usesCompactPurpleProfile &&
    (btnState.stateKey === 'bet' || btnState.stateKey === 'cashOut')
      ? buttonLabelByState[btnState.stateKey].toUpperCase()
      : buttonLabelByState[btnState.stateKey];
  const autoPlayButtonChrome = betPanelAutoControlsChrome?.autoPlayButton;
  const autoCashoutInputChrome = betPanelAutoControlsChrome?.autoCashoutInput;
  const autoCashoutLabelChrome = betPanelAutoControlsChrome?.autoCashoutLabel;
  const resolvedAutoCashoutToggleChrome =
    betPanelAutoControlsChrome?.autoCashoutToggle ?? autoCashoutToggleChrome;
  const isAutoCashoutInputEnabled =
    box.autoCashoutEnabled && !isAutoCashoutControlsDisabled;
  const isStakeInputEnabled = !isBetboxDisabled;
  const autoCashoutInputVisual = autoCashoutInputChrome
    ? resolveBetPanelInputVisual(
        autoCashoutInputChrome,
        isAutoCashoutInputEnabled
      )
    : null;
  const stakeInputVisual = autoCashoutInputChrome
    ? resolveBetPanelInputVisual(autoCashoutInputChrome, isStakeInputEnabled)
    : null;
  /** Auto Play button ON chrome only while autoplay is running. */
  const isAutoPlayButtonOnVisual = isAutoPlayActiveForThisSide;
  const autoPlayButtonVisual = autoPlayButtonChrome
    ? isAutoPlayButtonOnVisual
      ? autoPlayButtonChrome.on
      : autoPlayButtonChrome.off
    : null;

  const isStakeZero = isEditingStake
    ? (numberFormat.parseLocalizedNumber(localStakeValue) ?? 0) === 0
    : (box?.stake ?? 0) === 0;
  const isAutoCashoutZero = isEditingAutoCashout
    ? (numberFormat.parseLocalizedNumber(localAutoCashoutValue) ?? 0) === 0
    : (box?.autoCashoutX ?? 0) === 0;

  // Lock leaving Auto while Auto Play or Auto Cash Out is on.
  // Never block entering the Auto tab (reload used to restore ACO on Bet mode
  // and then disable both tabs via isTabSwitchDisabled).
  const isBetTabLocked = isAutoPlayActiveForThisSide || box.autoCashoutEnabled;
  const isAutoTabLocked = isAutoPlayActiveForThisSide;
  const getTabStyle = (mode: 'bet' | 'auto') => {
    const isActive = box.betMode === mode;
    const isDisabled =
      mode === 'bet'
        ? isBetTabLocked && !isActive
        : isAutoTabLocked && !isActive;

    return {
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? 'default' : 'pointer',
      ...(usesCompactPurpleProfile && !isActive
        ? {
            color: '#ffffff',
          }
        : {}),

      // ✅ ONLY override when active
      ...(isActive && {
        background: usesCompactPurpleProfile
          ? (betPanelStakeStepperChrome?.background ??
            'linear-gradient(180deg, #680bb9 100%, #a13ef8 0%)')
          : '#000000',
        color: '#ffffff',
      }),

      transition: 'background 0.2s ease',
    };
  };
  const handleStakeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const accepted = numberFormat.acceptLocalizedNumberInput(e.target.value);
    if (accepted !== null) {
      setLocalStakeValue(accepted);
      setIsEditingStake(true);
    }
  };

  const handleStakeBlur = () => {
    const numericValue = numberFormat.parseLocalizedNumber(
      localStakeValue.trim()
    );
    const nextStake = truncateToDecimals(
      numericValue === null || numericValue <= 0
        ? betLimits.minBet
        : clampStake(numericValue, betLimits)
    );

    useGameStore.getState().upBet(side, { stake: nextStake });
    setLocalStakeValue(numberFormat.formatEditableAmount(nextStake));
    setIsEditingStake(false);
  };

  const handleStakeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.key.length > 1) {
      return;
    }
    if (
      numberFormat.isLocalizedDecimalKey(e.key) &&
      localStakeValue.includes(decimal)
    ) {
      e.preventDefault();
      return;
    }
    if (!/[\d.,\s]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleStakeFocus = () => {
    setIsEditingStake(true);
    if (usesCompactPurpleProfile && box?.stake === 0) {
      setLocalStakeValue('');
    } else if (box?.stake !== undefined) {
      setLocalStakeValue(numberFormat.formatEditableAmount(box.stake));
    }
  };

  const handleAutoCashoutInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const accepted = numberFormat.acceptLocalizedNumberInput(e.target.value);
    if (accepted !== null) {
      setLocalAutoCashoutValue(accepted);
      setIsEditingAutoCashout(true);
    }
  };

  const handleAutoCashoutBlur = () => {
    const numericValue = numberFormat.parseLocalizedNumber(
      (autoCashoutInputRef.current?.value ?? localAutoCashoutValue).trim()
    );
    let constrainedValue: number;

    if (numericValue === null || numericValue < betLimits.minAutoCashout) {
      constrainedValue = betLimits.defaultAutoCashout;
    } else {
      constrainedValue = clampAutoCashout(numericValue, betLimits);
    }

    lastValidCashoutRef.current = constrainedValue;

    useGameStore.getState().upBet(side, { autoCashoutX: constrainedValue });
    setLocalAutoCashoutValue(
      numberFormat.formatEditableAmount(constrainedValue)
    );
    setIsEditingAutoCashout(false);
  };

  const handleAutoCashoutFocus = () => {
    setIsEditingAutoCashout(true);
  };

  const handleAutoCashoutClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAutoCashoutInputEnabled) return;

    setLocalAutoCashoutValue('');
    setIsEditingAutoCashout(true);
    requestAnimationFrame(() => {
      autoCashoutInputRef.current?.focus();
      autoCashoutInputRef.current?.select();
    });
  };

  const handleAutoCashoutKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.key.length > 1) {
      return;
    }
    if (
      numberFormat.isLocalizedDecimalKey(e.key) &&
      localAutoCashoutValue.includes(decimal)
    ) {
      e.preventDefault();
      return;
    }
    if (!/[\d.,\s]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleQuickBetClick = (value: number) => {
    const currentStake = box.stake;
    let newStake =
      lastQuickBet === value &&
      currentStake >= value &&
      currentStake % value === 0
        ? currentStake + value
        : value;

    controller
      .getGameState()
      .upBet(side, { stake: clampStake(newStake, betLimits) });
    setLastQuickBet(value);
    setIsEditingStake(false);
  };
  const handlePressStart = () => {
    // Clear any existing timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    } // Desktop: immediate visual feedback

    if (deviceType === 'desktop') {
      setPressed(true);
    } else {
      // Mobile/Tablet: visual feedback after 300ms hold
      longPressTimerRef.current = setTimeout(() => {
        setPressed(true);
      }, 300);
    }
  };

  const handlePressEnd = () => {
    // Clear timer if user releases before long press
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setPressed(false);
  };

  const handleClick = () => {
    // Only execute action if NOT disabled
    if (!btnState.disabled) {
      if (btnState.stateKey === 'bet') {
        handleAutoCashoutBlur();
      }
      btnState.action?.(); // --- FIX: Explicitly reset the 'pressed' state immediately after the action.
      // This prevents the mobile-press color (darker green) from lingering
      // when the button state (e.g., from 'armed' to 'idle' or 'cashed') changes.
      setPressed(false); // Also clear any potential long press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };

  mainBetTapDisabledRef.current = btnState.disabled;
  mainBetTapActionRef.current = handleClick;
  const mainBetPressHandlers = bindMainBetPress({
    onPressStart: handlePressStart,
    onPressEnd: handlePressEnd,
  });
  const handleMainBetKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!isSkyxNew) return;
    if (e.code === 'Space' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const mainBetTouchStyle: React.CSSProperties | undefined =
    useMainBetInstantTap ? { touchAction: 'manipulation' } : undefined;
  const valueTextStyle = (btnState as any).isSubtext
    ? subtextStyle
    : styles.mainBetAmountLabel;
  const mainBetChromeVariant =
    mainBetButtonChrome && btnState.stateKey === 'bet'
      ? mainBetButtonChrome.bet
      : mainBetButtonChrome && btnState.stateKey === 'cancel'
        ? mainBetButtonChrome.waiting
        : mainBetButtonChrome && btnState.stateKey === 'cashOut'
          ? mainBetButtonChrome.cashout
          : null;
  const isCompactPurplePrimaryBet =
    usesCompactPurpleProfile && btnState.stateKey === 'bet';
  const isCompactPurpleWaitingState =
    usesCompactPurpleProfile && btnState.stateKey === 'cancel';
  const isCompactPurpleCashoutState =
    usesCompactPurpleProfile && btnState.stateKey === 'cashOut';
  const skyxMainBetSoundAttrs =
    isSkyxNew && btnState.stateKey === 'cancel'
      ? { 'data-skyx-sound': 'cancel_popup' as const }
      : isSkyxNew && btnState.stateKey === 'cashOut'
        ? { 'data-skyx-click-sound': 'off' as const }
        : {};
  const renderedButtonText = displayButtonText;
  const shouldRenderValueLine = Boolean(btnState.valueText);
  const waitingSubtextStyle = isCompactPurpleWaitingState
    ? styles.mainBetWaitingLabel
    : valueTextStyle;
  const renderMainBetValueText = (text: unknown) => {
    if (typeof text !== 'string') return text as React.ReactNode;
    const displayUnit = formatCurrencyUnit(currency);
    const suffix = displayUnit ? ` ${displayUnit}` : '';
    if (usesCompactPurpleProfile && suffix && text.endsWith(suffix)) {
      const amount = text.slice(0, -suffix.length);
      const sharedFontSize = styles.mainBetAmountLabel.fontSize;
      return (
        <>
          <span style={{ fontWeight: 700, fontSize: sharedFontSize }}>
            {amount}
          </span>
          <span style={{ fontWeight: 400, fontSize: sharedFontSize }}>
            {suffix}
          </span>
        </>
      );
    }
    return text;
  };

  // ── Quick Bet Grid: strict exclusive rendering (single mounted version) ──
  const isImageMode = uiConfig?.quickBet?.type === 'image';

  const getQuickBetImagePath = (index: number) => {
    if (index === 1)
      return getGameAssetPath(gameSlug, 'QuickbetgridVariant3.png');
    if (index === 2)
      return getGameAssetPath(gameSlug, 'QuickbetgridVariant2.png');
    return getGameAssetPath(gameSlug, 'Quickbetgrid.png');
  };

  const formatQuickBetLabel = (value: number) =>
    Number.isInteger(value) ? String(value) : formatDecimal(value);

  const quickBetLabelStyle: React.CSSProperties = {
    fontFamily: styles.quickBetBtn.fontFamily ?? styles.stakeInput.fontFamily,
    fontWeight: styles.quickBetBtn.fontWeight ?? styles.stakeInput.fontWeight,
    fontSize: styles.quickBetBtn.fontSize ?? styles.stakeInput.fontSize,
    color: styles.quickBetBtn.color ?? styles.stakeInput.color,
    textRendering:
      styles.quickBetBtn.textRendering ?? styles.stakeInput.textRendering,
    fontVariantNumeric:
      styles.quickBetBtn.fontVariantNumeric ??
      styles.stakeInput.fontVariantNumeric,
    lineHeight: styles.quickBetBtn.lineHeight ?? styles.stakeInput.lineHeight,
    transform: styles.quickBetBtn.transform ?? styles.stakeInput.transform,
    WebkitFontSmoothing:
      styles.quickBetBtn.WebkitFontSmoothing ??
      styles.stakeInput.WebkitFontSmoothing,
    MozOsxFontSmoothing:
      styles.quickBetBtn.MozOsxFontSmoothing ??
      styles.stakeInput.MozOsxFontSmoothing,
  };

  const renderQuickBetImage = () => {
    return quickBetValues.map((v, index) => (
      <button
        key={v}
        style={{
          all: 'unset',
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height:
            styles.quickBetBtn.height || styles.quickBetBtn.minHeight || '24px',
          minHeight: styles.quickBetBtn.minHeight,
          borderRadius: styles.quickBetBtn.borderRadius || '64px',
          cursor: isBetboxDisabled ? 'default' : 'pointer',
          fontSize: styles.quickBetBtn.fontSize || '14px',
          lineHeight: '1',
          border: 'none',
          outline: 'none',
          appearance: 'none',
          WebkitAppearance: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          boxShadow: 'none',
          ...(isBetboxDisabled ? { opacity: 0.5 } : {}),
        }}
        onClick={() => !isBetboxDisabled && handleQuickBetClick(v)}
        disabled={isBetboxDisabled}
      >
        <img
          src={getQuickBetImagePath(index)}
          alt=""
          style={{
            position: 'absolute',
            top: -5.5,
            left: -6.5,
            width: '134%',
            height: '150%',
            objectFit: 'fill',
            borderRadius: 'inherit',
            pointerEvents: 'none',
            zIndex: 0,
            display: 'block',
          }}
          onError={e => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />

        <span
          style={{
            position: 'relative',
            zIndex: 1,
            ...quickBetLabelStyle,
            textShadow: usesCompactPurpleProfile
              ? undefined
              : '0 1px 3px rgba(0,0,0,0.7)',
          }}
        >
          {formatQuickBetLabel(v)}
        </span>
      </button>
    ));
  };

  const renderDefaultQuickBet = () => {
    return quickBetValues.map(v => (
      <button
        key={v}
        className={usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined}
        style={{
          ...styles.quickBetBtn,
          ...(usesCompactPurpleProfile
            ? {
                background: betPanelQuickBetChipChrome?.background ?? '#3c2193',
                border: `1px solid ${betPanelQuickBetChipChrome?.border ?? '#FFFFFF33'}`,
                color: betPanelQuickBetChipChrome?.color ?? '#ffffff',
                borderRadius:
                  betPanelQuickBetChipChrome?.borderRadius ??
                  styles.quickBetBtn.borderRadius,
                boxShadow: betPanelQuickBetChipChrome?.boxShadow ?? undefined,
              }
            : {}),
          ...(usesCompactPurpleProfile && isBetboxDisabled
            ? {
                background: betPanelStakeStepperChrome?.disabledBackground,
                color: betPanelStakeStepperChrome?.disabledColor,
                cursor: 'not-allowed',
              }
            : isBetboxDisabled
              ? { opacity: 1, cursor: 'default' }
              : {}),
        }}
        onClick={() => !isBetboxDisabled && handleQuickBetClick(v)}
        disabled={isBetboxDisabled}
      >
        {formatQuickBetLabel(v)}
      </button>
    ));
  };

  const renderQuickBetGrid = () => {
    if (isImageMode) {
      return (
        <div
          style={{
            ...styles.quickBetGrid,
            gap: styles.quickBetGrid.gap ?? 0,
            border: 'none',
            outline: 'none',
            background: 'none',
            boxShadow: 'none',
          }}
        >
          {renderQuickBetImage()}
        </div>
      );
    }

    return <div style={styles.quickBetGrid}>{renderDefaultQuickBet()}</div>;
  };

  return (
    <div
      data-bet-panel={isSkyxNew ? 'true' : undefined}
      style={{
        ...styles.betBoxControl,
        ...(usesCompactPurpleProfile
          ? {
              background: '#1F0A4D',
              border: '1px solid rgba(123,60,255,0.3)',
              borderRadius: '16px',
              boxShadow: 'none',
              height: '100%',
              minHeight: undefined,
            }
          : {}),
      }}
    >
      {isSkyxNew && betPanelButtonPressCss && (
        <style>{betPanelButtonPressCss}</style>
      )}
      {(phase === 'BET_LOCK' || phase === 'TAKEOFF' || phase === 'UNKNOWN') && (
        <div style={styles.disabledOverlay}>
          {/* You can optionally add text here, e.g., "Bets Locked" */}
        </div>
      )}
      {/* --- MODIFIED: Added opacity style for 'cashing' --- */}
      <div
        style={{
          ...styles.betBox,

          //  SKY SNIFFER → IMAGE STYLE
          ...(isSkySniffer
            ? {
                backgroundImage: `url(${betPanelBg})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }
            : //  ALL OTHER GAMES → DEFAULT STYLE (NO IMAGE)
              {
                backgroundImage: 'none',
                backgroundColor: styles.betBox.backgroundColor,
                border: styles.betBox.border,
                boxShadow: styles.betBox.boxShadow,
              }),

          //  COMMON
          ...(box.status === 'cashing' || box.status === 'cancelling'
            ? { opacity: 0.7 }
            : {}),
        }}
      >
        <div style={styles.betBoxTabs}>
          <div
            style={{
              ...styles.betModeToggle,
              ...(usesCompactPurpleProfile
                ? {
                    background: '#18073D',
                    border: '1px solid rgba(123,60,255,0.35)',
                  }
                : {}),
            }}
          >
            <button
              onClick={() => {
                if (!isBetTabLocked) {
                  setIsAuto(false);
                  useGameStore.getState().upBet(side, {
                    betMode: 'bet',
                    autoCashoutX: 1.1,
                    autoCashoutEnabled: false,
                  });
                }
              }}
              style={{
                ...styles.betBoxTab,
                ...(box.betMode === 'bet' ? styles.betBoxTabSlider : {}),
                ...getTabStyle('bet'),
              }}
              className={
                usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined
              }
              aria-pressed={box.betMode === 'bet'}
              disabled={isBetTabLocked}
            >
              {t('ui.control_panel.bet_panel.bet')}
            </button>

            <button
              onClick={() => {
                if (!isAutoTabLocked) {
                  setIsAuto(true);
                  useGameStore.getState().upBet(side, {
                    betMode: 'auto',
                    autoCashoutX: box.autoCashoutX || 1.1,
                  });
                }
              }}
              style={{
                ...styles.betBoxTab,
                ...(box.betMode === 'auto' ? styles.betBoxTabSlider : {}),
                ...getTabStyle('auto'),
              }}
              className={
                usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined
              }
              aria-pressed={box.betMode === 'auto'}
              disabled={isAutoTabLocked}
            >
              {t('ui.control_panel.auto.auto')}
            </button>
          </div>
        </div>

        <div style={styles.middleRow}>
          <div style={styles.stakeColumn}>
            <div style={styles.stakeContainer}>
              <div
                style={{
                  ...styles.stakeInputWrap,
                  ...(stakeInputVisual
                    ? {
                        backgroundColor: 'transparent',
                        border: 'none',
                        padding: '0 2px',
                      }
                    : usesCompactPurpleProfile
                      ? {
                          backgroundColor: 'transparent',
                          border: 'none',
                        }
                      : {}),
                }}
              >
                {/* MINUS BUTTON */}
                <button
                  className={
                    usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined
                  }
                  style={{
                    ...styles.stakeBtn,
                    ...(usesCompactPurpleProfile
                      ? {
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          background:
                            betPanelStakeStepperChrome?.background ??
                            styles.stakeBtn.background,
                          border: 'none',
                          boxShadow: undefined,
                          color: betPanelStakeStepperChrome?.color ?? '#ffffff',
                        }
                      : {}),
                    ...(isMinusActive &&
                    !isStakeDecreaseDisabled &&
                    !usesCompactPurpleProfile
                      ? activeStyle
                      : {}),
                    ...(usesCompactPurpleProfile && isStakeDecreaseDisabled
                      ? {
                          background:
                            betPanelStakeStepperChrome?.disabledBackground,
                          color: betPanelStakeStepperChrome?.disabledColor,
                          cursor: 'not-allowed',
                        }
                      : isStakeDecreaseDisabled
                        ? { opacity: 0.5, cursor: 'default' }
                        : {}),
                  }}
                  onMouseDown={e => {
                    if (!isStakeDecreaseDisabled) {
                      setIsMinusActive(true);
                      decreasePressHandlers.onMouseDown(e);
                    }
                  }}
                  onMouseUp={() => {
                    setIsMinusActive(false);
                    decreasePressHandlers.onMouseUp();
                  }}
                  onMouseLeave={() => {
                    setIsMinusActive(false);
                    decreasePressHandlers.onMouseLeave();
                  }}
                  onTouchStart={e => {
                    if (!isStakeDecreaseDisabled) {
                      setIsMinusActive(true);
                      decreasePressHandlers.onTouchStart(e);
                    }
                  }}
                  onTouchEnd={() => {
                    setIsMinusActive(false);
                    decreasePressHandlers.onTouchEnd();
                  }}
                  onTouchCancel={() => {
                    setIsMinusActive(false);
                    decreasePressHandlers.onTouchCancel();
                  }}
                  onTouchMove={decreasePressHandlers.onTouchMove}
                  disabled={isStakeDecreaseDisabled}
                >
                  <img
                    src={autoplayMinusImageUrl}
                    alt={t('ui.control_panel.bet_panel.decrease_stake')}
                    style={{
                      maxWidth: stakeBtnIconSize,
                      maxHeight: stakeBtnIconSize,
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      filter: isStakeDecreaseDisabled
                        ? 'grayscale(1)'
                        : usesCompactPurpleProfile
                          ? 'brightness(0) invert(1)'
                          : 'none',
                    }}
                  />
                </button>

                <input
                  type="text"
                  inputMode="decimal"
                  lang={numberFormat.locale}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-skyx-typing-sound=""
                  value={localStakeValue}
                  onChange={handleStakeInputChange}
                  onBlur={handleStakeBlur}
                  onFocus={handleStakeFocus}
                  onKeyDown={handleStakeKeyDown}
                  style={{
                    ...styles.stakeInput,
                    ...(usesCompactPurpleProfile && betPanelStakeInputChrome
                      ? {
                          background: betPanelStakeInputChrome.background,
                          border: `1px solid ${
                            !isStakeZero
                              ? betPanelStakeInputChrome.nonZeroBorder
                              : isEditingStake
                                ? betPanelStakeInputChrome.focusBorder
                                : betPanelStakeInputChrome.border
                          }`,
                          borderRadius: '9px',
                          height: '32px',
                          lineHeight: '30px',
                          padding: '0 8px',
                          boxSizing: 'border-box',
                          textAlign: 'center',
                          color: betPanelStakeInputChrome.color,
                          opacity: isBetboxDisabled ? 0.5 : 1,
                          boxShadow: !isStakeZero
                            ? betPanelStakeInputChrome.nonZeroBoxShadow
                            : isEditingStake
                              ? betPanelStakeInputChrome.focusBoxShadow
                              : 'none',
                          caretColor: betPanelStakeInputChrome.color,
                        }
                      : stakeInputVisual
                        ? {
                            background: stakeInputVisual.background,
                            border: `1px solid ${stakeInputVisual.border}`,
                            borderRadius: '8px',
                            color: stakeInputVisual.color,
                            opacity: stakeInputVisual.opacity,
                            boxShadow: stakeInputVisual.boxShadow,
                            caretColor: stakeInputVisual.color,
                          }
                        : isBetboxDisabled
                          ? { opacity: 0.5 }
                          : {}),
                    margin: usesCompactPurpleProfile ? '0 1px' : '0 2px',
                  }}
                  disabled={isBetboxDisabled}
                />

                <button
                  className={
                    usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined
                  }
                  style={{
                    ...styles.stakeBtn,
                    ...(usesCompactPurpleProfile
                      ? {
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          background:
                            betPanelStakeStepperChrome?.background ??
                            styles.stakeBtn.background,
                          border: 'none',
                          boxShadow: undefined,
                          color: betPanelStakeStepperChrome?.color ?? '#ffffff',
                        }
                      : {}),
                    ...(isPlusActive &&
                    !isStakeIncreaseDisabled &&
                    !usesCompactPurpleProfile
                      ? activeStyle
                      : {}),
                    ...(usesCompactPurpleProfile && isStakeIncreaseDisabled
                      ? {
                          background:
                            betPanelStakeStepperChrome?.disabledBackground,
                          color: betPanelStakeStepperChrome?.disabledColor,
                          cursor: 'not-allowed',
                        }
                      : isStakeIncreaseDisabled
                        ? { opacity: 0.5, cursor: 'default' }
                        : {}),
                  }}
                  onMouseDown={e => {
                    if (!isStakeIncreaseDisabled) {
                      setIsPlusActive(true);
                      increasePressHandlers.onMouseDown(e);
                    }
                  }}
                  onMouseUp={() => {
                    setIsPlusActive(false);
                    increasePressHandlers.onMouseUp();
                  }}
                  onMouseLeave={() => {
                    setIsPlusActive(false);
                    increasePressHandlers.onMouseLeave();
                  }}
                  onTouchStart={e => {
                    if (!isStakeIncreaseDisabled) {
                      setIsPlusActive(true);
                      increasePressHandlers.onTouchStart(e);
                    }
                  }}
                  onTouchEnd={() => {
                    setIsPlusActive(false);
                    increasePressHandlers.onTouchEnd();
                  }}
                  onTouchCancel={() => {
                    setIsPlusActive(false);
                    increasePressHandlers.onTouchCancel();
                  }}
                  onTouchMove={increasePressHandlers.onTouchMove}
                  disabled={isStakeIncreaseDisabled}
                >
                  <img
                    src={autoplayPlusImageUrl}
                    alt={t('ui.control_panel.bet_panel.increase_stake')}
                    style={{
                      maxWidth: stakeBtnIconSize,
                      maxHeight: stakeBtnIconSize,
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      filter: isStakeIncreaseDisabled
                        ? 'grayscale(1)'
                        : usesCompactPurpleProfile
                          ? 'brightness(0) invert(1)'
                          : 'none',
                    }}
                  />
                </button>
              </div>
            </div>

            {renderQuickBetGrid()}
          </div>

          <div
            style={{
              ...styles.mainBetContainer,
              ...(usesCompactPurpleProfile
                ? {
                    border: betPanelBorderless ? 'none' : '1px solid #3c2193',
                    background: 'transparent',
                    borderRadius: '12px',
                  }
                : {}),
              ...(isSkySniffer && uiConfig?.betButton?.type === 'image'
                ? {
                    padding: 0,
                    border: 'none',
                    overflow: 'hidden',
                  }
                : {}),
            }}
          >
            {uiConfig?.betButton?.type === 'image' ? (
              (() => {
                const imageFile =
                  BET_BUTTON_IMAGE_BY_STATE[btnState.stateKey] ??
                  BET_BUTTON_IMAGE_BY_STATE.bet;
                const imgPath = getGameAssetPath(gameSlug, imageFile);
                return (
                  <button
                    type="button"
                    {...skyxMainBetSoundAttrs}
                    style={{
                      ...styles.mainBetBtn,
                      ...mainBetTouchStyle,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      overflow: 'hidden',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      minHeight: 0,
                      borderRadius: 'inherit',
                      ...(btnState.disabled ? styles.mainBetBtnDisabled : {}),
                      outline: 'none',
                    }}
                    onMouseEnter={() => {
                      if (isMainBetHoverEnabled) setHovered(true);
                    }}
                    onMouseLeave={() => {
                      setHovered(false);
                      setPressed(false);
                      mainBetPressHandlers.onMouseLeave?.();
                    }}
                    onMouseDown={mainBetPressHandlers.onMouseDown}
                    onMouseUp={mainBetPressHandlers.onMouseUp}
                    onTouchStart={mainBetPressHandlers.onTouchStart}
                    onTouchMove={mainBetPressHandlers.onTouchMove}
                    onTouchEnd={mainBetPressHandlers.onTouchEnd}
                    onTouchCancel={mainBetPressHandlers.onTouchCancel}
                    onPointerDown={mainBetPressHandlers.onPointerDown}
                    onPointerMove={mainBetPressHandlers.onPointerMove}
                    onPointerUp={mainBetPressHandlers.onPointerUp}
                    onPointerCancel={mainBetPressHandlers.onPointerCancel}
                    onClick={mainBetPressHandlers.onClick}
                    onKeyDown={handleMainBetKeyDown}
                    disabled={btnState.disabled}
                  >
                    <img
                      src={imgPath}
                      alt={displayButtonText}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'fill',
                        display: 'block',
                        pointerEvents: 'none',
                        borderRadius: 'inherit',
                      }}
                      onError={e => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: mainBetHovered ? '#fff' : '#0E1114',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: 18,
                        pointerEvents: 'none',
                        zIndex: 1,
                        transition: 'color 0.25s ease',
                      }}
                    >
                      <span
                        style={{
                          maxWidth: '100%',
                          padding: '0 8px',
                          textAlign: 'center',
                          lineHeight: 1.15,
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          whiteSpace: 'normal',
                        }}
                      >
                        {renderedButtonText}
                      </span>
                      {shouldRenderValueLine && btnState.valueText ? (
                        <span
                          style={
                            isCompactPurpleWaitingState
                              ? styles.mainBetWaitingLabel
                              : (btnState as { isSubtext?: boolean }).isSubtext
                                ? subtextStyle
                                : {
                                    fontSize: 20,
                                    fontWeight: 500,
                                  }
                          }
                        >
                          {btnState.valueText}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })()
            ) : (
              <button
                type="button"
                className={
                  usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined
                }
                {...skyxMainBetSoundAttrs}
                style={{
                  ...styles.mainBetBtn,
                  ...mainBetTouchStyle,
                  ...(usesCompactPurpleProfile && mainBetChromeVariant
                    ? {
                        background: mainBetChromeVariant.background,
                        border: 'none',
                        boxShadow: undefined,
                        filter: undefined,
                      }
                    : mainBetChromeVariant
                      ? resolveMainBetChromeButtonStyle(
                          mainBetChromeVariant,
                          mainBetHovered,
                          pressed
                        )
                      : {
                          background: isCompactPurplePrimaryBet
                            ? 'linear-gradient(180deg, #60ce12 0%, #349809 100%)'
                            : isCompactPurpleWaitingState
                              ? 'linear-gradient(180deg, #f01f1f 0%, #980909 100%)'
                              : isCompactPurpleCashoutState
                                ? 'linear-gradient(180deg, #fff21e 0%, #b88b00 100%)'
                                : btnState.color,
                          borderColor: (btnState as any).borderColor,
                          boxShadow: btnState.boxShadow,
                          border: isCompactPurplePrimaryBet
                            ? '1px solid #73ff49'
                            : isCompactPurpleWaitingState
                              ? '1px solid #ea3434'
                              : isCompactPurpleCashoutState
                                ? '1px solid #fff251'
                                : pressed
                                  ? 'none'
                                  : '1px solid ' +
                                    (btnState as any).borderColor,
                        }),
                  ...(btnState.disabled ? styles.mainBetBtnDisabled : {}),
                  outline: 'none',
                  color: '#ffffff',
                }}
                onMouseEnter={() => {
                  if (isMainBetHoverEnabled) setHovered(true);
                }}
                onMouseLeave={() => {
                  setHovered(false);
                  setPressed(false);
                  mainBetPressHandlers.onMouseLeave?.();
                }}
                onMouseDown={mainBetPressHandlers.onMouseDown}
                onMouseUp={mainBetPressHandlers.onMouseUp}
                onTouchStart={mainBetPressHandlers.onTouchStart}
                onTouchMove={mainBetPressHandlers.onTouchMove}
                onTouchEnd={mainBetPressHandlers.onTouchEnd}
                onTouchCancel={mainBetPressHandlers.onTouchCancel}
                onPointerDown={mainBetPressHandlers.onPointerDown}
                onPointerMove={mainBetPressHandlers.onPointerMove}
                onPointerUp={mainBetPressHandlers.onPointerUp}
                onPointerCancel={mainBetPressHandlers.onPointerCancel}
                onClick={mainBetPressHandlers.onClick}
                onKeyDown={handleMainBetKeyDown}
                disabled={btnState.disabled}
              >
                <div style={styles.mainBetTopLabel}>{renderedButtonText}</div>
                {shouldRenderValueLine ? (
                  <div style={waitingSubtextStyle}>
                    {renderMainBetValueText(btnState.valueText)}
                  </div>
                ) : null}
              </button>
            )}
          </div>
        </div>
      </div>
      {box.betMode === 'auto' && (
        <div
          style={{
            ...styles.borderLine,
            ...(usesCompactPurpleProfile ? { background: '#3c2193' } : {}),
          }}
        ></div>
      )}
      {box.betMode === 'auto' && (
        <div
          style={{
            ...styles.autoBetContainer,
            ...(usesCompactPurpleProfile
              ? {
                  padding: styles.betBox.padding,
                  boxSizing: 'border-box',
                }
              : {}),
          }}
        >
          <div
            style={{
              ...styles.autoBetWrapper,
              ...(centerAutoCashout ? { justifyContent: 'center' } : {}),
            }}
          >
            {box.betMode === 'auto' && autoplayAllowed && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className={
                    usesCompactPurpleProfile ? 'bet-panel-3d-btn' : undefined
                  }
                  style={{
                    ...styles.autoPlayButton,
                    ...(autoPlayButtonOffset ?? {}),
                    ...(autoPlayButtonVisual
                      ? {
                          backgroundColor: 'transparent',
                          background: autoPlayButtonVisual.background,
                          border: `1px solid ${autoPlayButtonVisual.border}`,
                          color: autoPlayButtonVisual.color,
                          boxShadow:
                            autoPlayButtonVisual.boxShadow ?? undefined,
                        }
                      : {
                          background: isAutoPlayActiveForThisSide
                            ? (theme.colors.danger ?? '#f44336')
                            : isSkyxNew
                              ? 'linear-gradient(90deg, #FE5000 0%, #FDA33F 100%)'
                              : (theme.colors.autoplayBtBg ?? '#1cadda'),
                          border: '1px solid',
                          borderColor: isAutoPlayActiveForThisSide
                            ? (theme.colors.cancelborder ?? '#ff0000')
                            : isSkyxNew
                              ? '#F3BC71'
                              : (theme.colors.autoplayBtBgborder ?? '#0aa3d2'),
                        }),
                  }}
                  onClick={() => {
                    if (!autoplayAllowed) return;
                    if (isAutoPlayActiveForThisSide) {
                      useGameStore.getState().stopAutoPlay(side);
                    } else {
                      useGameStore.getState().openAutoPlayModal(side);
                    }
                  }}
                  onMouseOver={e => {
                    if (autoPlayButtonVisual) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.background =
                        autoPlayButtonVisual.background;
                      e.currentTarget.style.border = `1px solid ${autoPlayButtonVisual.border}`;
                      e.currentTarget.style.color = autoPlayButtonVisual.color;
                      e.currentTarget.style.boxShadow =
                        autoPlayButtonVisual.boxShadow ?? 'none';
                      if (!isAutoPlayButtonOnVisual) {
                        e.currentTarget.style.filter = 'brightness(1.06)';
                      }
                      return;
                    }
                    if (isAutoPlayActiveForThisSide) {
                      e.currentTarget.style.backgroundColor =
                        theme.colors.betCancelHover ?? '#d32f2f';
                    } else if (isSkyxNew) {
                      e.currentTarget.style.background =
                        'linear-gradient(90deg, #FE5000 0%, #FDA33F 100%)';
                      e.currentTarget.style.filter = 'brightness(1.06)';
                    } else {
                      e.currentTarget.style.backgroundColor =
                        theme.colors.autoplayBgHover ?? '#398ba4ff';
                    }
                  }}
                  onMouseLeave={e => {
                    if (autoPlayButtonVisual) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.background =
                        autoPlayButtonVisual.background;
                      e.currentTarget.style.border = `1px solid ${autoPlayButtonVisual.border}`;
                      e.currentTarget.style.color = autoPlayButtonVisual.color;
                      e.currentTarget.style.boxShadow =
                        autoPlayButtonVisual.boxShadow ?? 'none';
                      e.currentTarget.style.filter = 'none';
                      return;
                    }
                    if (isAutoPlayActiveForThisSide) {
                      e.currentTarget.style.backgroundColor =
                        theme.colors.danger ?? '#f44336';
                    } else if (isSkyxNew) {
                      e.currentTarget.style.background =
                        'linear-gradient(90deg, #FE5000 0%, #FDA33F 100%)';
                      e.currentTarget.style.filter = 'none';
                    } else {
                      e.currentTarget.style.backgroundColor =
                        theme.colors.autoplayBtBg ?? '#1cadda';
                    }
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      top:
                        autoPlayTextOffset?.top ??
                        (deviceType === 'desktop' ? '-1.5px' : '-1px'),
                      left:
                        autoPlayTextOffset?.left ??
                        (deviceType === 'desktop' ? '-1.5px' : '-1px'),
                      fontWeight: autoPlayButtonVisual ? 600 : '490',
                      fontSize: usesCompactPurpleProfile ? '11px' : '10px',
                      lineHeight: 1.15,
                      maxWidth: '100%',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      color: autoPlayButtonVisual?.color,
                    }}
                  >
                    {isAutoPlayActiveForThisSide && autoPlayProgress
                      ? autoPlaySettings?.rounds !== null
                        ? (() => {
                            const remaining =
                              autoPlaySettings!.rounds -
                              autoPlayProgress.roundsPlayed; // --- THIS IS THE REVERTED/CORRECTED LINE ---
                            return remaining <= 0
                              ? t('ui.common.stop')
                              : t('ui.control_panel.auto.stop_remaining', {
                                  count: remaining,
                                });
                          })()
                        : t('ui.control_panel.auto.stop_auto')
                      : t('ui.control_panel.auto.auto_play')}
                  </span>
                </button>
              </div>
            )}

            <div
              style={{
                ...styles.cashoutBlock,
                ...(usesCompactPurpleProfile
                  ? { maxWidth: styles.stakeColumn.maxWidth }
                  : {}),
                ...(centerAutoCashout
                  ? {
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: usesCompactPurpleProfile ? '8px' : '10px',
                      margin: 0,
                      marginInline: 'auto',
                      left: 0,
                      maxWidth: '100%',
                      width: '100%',
                      boxSizing: 'border-box',
                    }
                  : {}),
              }}
            >
              <div
                style={{
                  ...toggleStyles.container,
                  display: 'flex',
                  alignItems: 'center',
                  ...(centerAutoCashout
                    ? {
                        width: 'auto',
                        flex: '0 0 auto',
                        justifyContent: 'flex-start',
                        left: 0,
                        position: 'relative',
                      }
                    : {
                        left:
                          autoCashoutToggleLeft ??
                          (deviceType === 'mobile' ? '14px' : '19px'),
                        position: 'relative',
                      }),
                  ...(isAutoCashoutControlsDisabled
                    ? { opacity: 0.5, cursor: 'default' }
                    : { cursor: 'pointer' }),
                }}
                onClick={() => {
                  if (!isAutoCashoutControlsDisabled) {
                    useGameStore.getState().upBet(side, {
                      autoCashoutEnabled: !box.autoCashoutEnabled,
                    });
                  }
                }}
              >
                <div
                  style={{
                    ...toggleStyles.switch,
                    ...(usesCompactPurpleProfile
                      ? {
                          width: '30px',
                          height: '17px',
                          padding: '2px',
                        }
                      : {}),
                    background: box.autoCashoutEnabled
                      ? (resolvedAutoCashoutToggleChrome?.trackOn ??
                        toggleStyles.switch.backgroundColor)
                      : (resolvedAutoCashoutToggleChrome?.trackOff ??
                        toggleStyles.switch.backgroundColor),
                    boxShadow:
                      box.autoCashoutEnabled &&
                      resolvedAutoCashoutToggleChrome?.trackOnGlow
                        ? resolvedAutoCashoutToggleChrome.trackOnGlow
                        : undefined,
                    // --- UPDATED CURSOR LOGIC HERE ---
                    cursor: isAutoCashoutControlsDisabled
                      ? 'default'
                      : 'pointer',
                  }}
                >
                  <span
                    style={{
                      ...toggleStyles.slidderInput,
                      ...(usesCompactPurpleProfile
                        ? { minWidth: '13px', maxWidth: '13px', height: '13px' }
                        : {}),
                      backgroundColor: box.autoCashoutEnabled
                        ? (resolvedAutoCashoutToggleChrome?.thumbOn ??
                          '#a3a3a3')
                        : (resolvedAutoCashoutToggleChrome?.thumbOff ??
                          '#2c2d30'),
                      transform: box.autoCashoutEnabled
                        ? usesCompactPurpleProfile
                          ? 'translateX(13px)'
                          : 'translateX(12px)'
                        : 'translateX(0)',
                      transition: 'all 0.3s ease',
                    }}
                  ></span>
                </div>

                <label
                  style={{
                    ...styles.autoLabel,
                    ...(usesCompactPurpleProfile
                      ? { marginLeft: '8px', marginRight: 0 }
                      : {}),
                    ...(autoCashoutLabelChrome
                      ? {
                          color: box.autoCashoutEnabled
                            ? autoCashoutLabelChrome.on
                            : autoCashoutLabelChrome.off,
                        }
                      : {}),
                  }}
                >
                  {t('ui.control_panel.auto.auto_cash_out')}
                </label>
              </div>

              <div
                style={{
                  ...styles.cashoutInput,
                  ...(usesCompactPurpleProfile && betPanelStakeInputChrome
                    ? {
                        backgroundColor: betPanelStakeInputChrome.background,
                        border: `1px solid ${
                          box.autoCashoutEnabled && !isAutoCashoutZero
                            ? betPanelStakeInputChrome.nonZeroBorder
                            : isEditingAutoCashout
                              ? betPanelStakeInputChrome.focusBorder
                              : betPanelStakeInputChrome.border
                        }`,
                        boxShadow:
                          box.autoCashoutEnabled && !isAutoCashoutZero
                            ? betPanelStakeInputChrome.nonZeroBoxShadow
                            : isEditingAutoCashout
                              ? betPanelStakeInputChrome.focusBoxShadow
                              : 'none',
                        opacity: box.autoCashoutEnabled ? 1 : 0.5,
                        borderRadius: '9px',
                      }
                    : autoCashoutInputVisual
                      ? {
                          backgroundColor: autoCashoutInputVisual.background,
                          border: `1px solid ${autoCashoutInputVisual.border}`,
                          opacity: autoCashoutInputVisual.opacity,
                          boxShadow: autoCashoutInputVisual.boxShadow,
                          borderRadius: '8px',
                        }
                      : usesCompactPurpleProfile
                        ? {
                            backgroundColor: '#170550',
                            border: betPanelBorderless
                              ? 'none'
                              : '1px solid #3c2193',
                          }
                        : {}),
                  ...(centerAutoCashout
                    ? {
                        width: 'auto',
                        minWidth: usesCompactPurpleProfile ? '72px' : '80px',
                        maxWidth: usesCompactPurpleProfile ? '96px' : '110px',
                        flex: '0 0 auto',
                        marginLeft: 0,
                      }
                    : {}),
                }}
              >
                <div style={styles.cashoutInputWrapper}>
                  <input
                    ref={autoCashoutInputRef}
                    type="text"
                    inputMode="decimal"
                    lang={numberFormat.locale}
                    step="0.01"
                    data-skyx-typing-sound=""
                    value={localAutoCashoutValue}
                    onChange={handleAutoCashoutInputChange}
                    onBlur={handleAutoCashoutBlur}
                    onFocus={handleAutoCashoutFocus}
                    onKeyDown={handleAutoCashoutKeyDown}
                    style={{
                      ...styles.autoInput,
                      ...(usesCompactPurpleProfile
                        ? { height: styles.stakeInput.height }
                        : { height: '25px' }),
                      color: autoCashoutInputVisual?.color,
                      caretColor:
                        autoCashoutInputVisual?.color ??
                        styles.stakeInput.caretColor,
                      opacity: autoCashoutInputChrome
                        ? 1
                        : box.autoCashoutEnabled &&
                            !isAutoCashoutControlsDisabled
                          ? 1
                          : 0.5,
                      paddingRight: usesCompactPurpleProfile ? 0 : '20px',
                    }}
                    disabled={
                      !box.autoCashoutEnabled || isAutoCashoutControlsDisabled
                    }
                    min={String(betLimits.minAutoCashout)}
                    max={String(betLimits.maxAutoCashout)}
                    autoComplete="off"
                    tabIndex={0}
                  />

                  <button
                    type="button"
                    data-skyx-autocashout-clear
                    onMouseDown={handleAutoCashoutClear}
                    disabled={!isAutoCashoutInputEnabled}
                    aria-label={t('ui.control_panel.auto.clear_auto_cash_out')}
                    style={{
                      ...styles.autoInputX,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      lineHeight: 1,
                      color: autoCashoutInputChrome
                        ? isAutoCashoutInputEnabled
                          ? autoCashoutInputChrome.multiplierColorEnabled
                          : autoCashoutInputChrome.multiplierColorDisabled
                        : styles.autoInputX.color,
                      opacity: autoCashoutInputChrome
                        ? 1
                        : box.autoCashoutEnabled &&
                            !isAutoCashoutControlsDisabled
                          ? 1
                          : 0.5,
                      cursor: isAutoCashoutInputEnabled ? 'pointer' : 'default',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
